import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../shared/notifiers/base-notifier';
import { Ref } from '../../shared/providers/ref';
import { MapProvider } from '../providers/map-provider';
import { LayerProvider } from '../providers/layer-provider';
import { FullExtentProvider } from '../providers/full-extent-provider';
import { PopupInteractionProvider } from '../providers/popup-interaction-provider';
import { ArcGisQueryRepositoryProvider } from '../../shared/providers/repository-providers';
import { LoadingProvider } from '../../shared/providers/loading-provider';
import { CommunityIntersectionService, LayerTarget } from '../services/community-intersection-service';
import { CommunityProfileService } from '../services/community-profile-service';
import { CommunitySearchState } from '../states/community-search-state';
import { ExecutiveProvider } from '../providers/executive-provider';
import { RightPanelProvider } from '../providers/right-panel-provider';
import { UbigeoProvider } from '../providers/ubigeo-provider';
import { InterventionProfileService } from '../services/intervention-profile-service';
import { FeatureEntity, GeolocatorEntity } from '@gishub/domain';
import Polygon from '@arcgis/core/geometry/Polygon';
import Point from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';

const COMMUNITY_SOURCES = [
  { name: 'Comunidades campesinas', abbreviation: 'CCCC', url: 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/9', displayField: 'nom_comunidad' },
  { name: 'Comunidades nativas', abbreviation: 'CCNN', url: 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/10', displayField: 'nom_comunidad' },
];

const INTERSECTION_TARGETS: LayerTarget[] = [
  { key: 'intervenciones', label: 'Intervenciones sectoriales', badge: 'IS', url: 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/6', hasProxy: false },
  { key: 'compromisos', label: 'Compromisos agrarios', badge: 'CA', url: 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/1', hasProxy: false },
  { key: 'espacios', label: 'Espacios de diálogo', badge: 'ED', url: 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/4', hasProxy: false },
];

@Injectable({ providedIn: 'root' })
export class CommunitySearchNotifier extends BaseNotifier<CommunitySearchState> {
  private highlightGraphics: Graphic[] = [];
  private pulseInterval: any = null;
  private _mapClickHandle: { remove: () => void } | null = null;
  private _lastViewForClick: any = null;
  private _cache: GeolocatorEntity[] = [];
  private _cacheLoading = false;

  private _lastUbigeoGeomKey: string = '';

  constructor(ref: Ref) {
    super(ref, { results: [], loading: false, selectedResult: null });

    this._loadCache();

    this.ref.createEffect(() => {
      const mapState = this.ref.read(MapProvider).state();
      const view = mapState.view;
      if (view && view !== this._lastViewForClick) {
        this._removeMapClickHandler();
        this._lastViewForClick = view;
        this._mapClickHandle = view.on('click', (event: any) => this._handleMapClick(event));
      } else if (!view) {
        this._removeMapClickHandler();
      }
    });

    // Re-query communities when Ubigeo geometry changes
    this.ref.createEffect(() => {
      const geom = this.ref.read(UbigeoProvider).state().currentGeometry;
      const key = geom ? JSON.stringify(geom.rings ?? geom.x) : '';
      if (key && key !== this._lastUbigeoGeomKey) {
        this._lastUbigeoGeomKey = key;
        this._updateResults();
      }
    });
  }

  private get queryRepo() { return this.ref.read(ArcGisQueryRepositoryProvider).instance; }
  private get mapProvider() { return this.ref.read(MapProvider); }
  private get layerProvider() { return this.ref.read(LayerProvider); }
  private get fullExtent() { return this.ref.read(FullExtentProvider).notifier; }
  private get intersectionService() { return this.ref.read(CommunityIntersectionService); }
  private get communityProfileService() { return this.ref.read(CommunityProfileService); }
  private get executive() { return this.ref.read(ExecutiveProvider).notifier; }
  private get rightPanel() { return this.ref.read(RightPanelProvider); }
  private get popupInteraction() { return this.ref.read(PopupInteractionProvider); }
  private get loading() { return this.ref.read(LoadingProvider); }
  private get interventionProfile() { return this.ref.read(InterventionProfileService); }

  private _isLayerVisible(layerName: string): boolean {
    const layers = this.layerProvider.state().layers;
    for (const cat of layers) {
      if (!cat.layers) continue;
      for (const sub of cat.layers) {
        if (sub.name === layerName) return !!(cat.visible && sub.visible);
      }
    }
    return false;
  }

  private _removeMapClickHandler(): void {
    if (this._mapClickHandle) {
      this._mapClickHandle.remove();
      this._mapClickHandle = null;
    }
    this._lastViewForClick = null;
  }

  private async _handleMapClick(event: any): Promise<void> {
    const mp = event.mapPoint;
    if (!mp) return;

    if (this.popupInteraction.state().enabled) return;

    // If an intervention was just selected, skip community search
    if (this.interventionProfile.active()) {
      this.interventionProfile.clear();
      return;
    }

    // Only process map click if at least one community layer is visible
    const ccVisible = this._isLayerVisible('Comunidades campesinas');
    const cnVisible = this._isLayerVisible('Comunidades nativas');
    if (!ccVisible && !cnVisible) return;

    const sr = mp.spatialReference?.wkid ?? 4326;
    const dptoFilter = "(UPPER(nom_dpto) LIKE '%JUNIN%' OR UPPER(nom_dpto) LIKE '%JUNÍN%')";

    const current = this.getStateValue().selectedResult;
    let hit = false;

    for (const source of COMMUNITY_SOURCES) {
      try {
        const result = await this.queryRepo.queryLayerFeatures(
          source.url,
          dptoFilter,
          { x: mp.x, y: mp.y, spatialReference: { wkid: sr } },
          'esriGeometryPoint',
          'esriSpatialRelIntersects',
          '*',
          true,
          0,
          1,
          false,
        );
        // If an intervention was selected while the query was in flight, yield
        if (this.interventionProfile.active()) return;
        if (!result.success || !result.data || result.data.length === 0) continue;
        const f = result.data[0];
        const name = String(f.attributes[source.displayField] ?? '').trim();
        if (!name || name.length < 3) continue;
        const id = `${source.name}_${f.attributes.OBJECTID ?? f.attributes.objectid ?? 0}`;
        if (current?.id === id) {
          this.clearSearch();
          return;
        }
        const entity: GeolocatorEntity = {
          id,
          name,
          sourceName: source.name,
          geometry: f.geometry,
          attributes: f.attributes as Record<string, any>,
        };
        this.selectResult(entity);
        hit = true;
        return;
      } catch { /* next source */ }
    }

    if (!hit && current) {
      this.clearSearch();
    }
  }

  private async _updateResults(): Promise<void> {
    const ubigeo = this.ref.read(UbigeoProvider).state();
    const geom = ubigeo.currentGeometry;
    if (!geom) {
      this.setState({ ...this.getStateValue(), results: [], selectedResult: null });
      return;
    }

    this.setState({ ...this.getStateValue(), loading: true });
    const all: GeolocatorEntity[] = [];

    for (const source of COMMUNITY_SOURCES) {
      try {
        const res = await this.queryRepo.queryLayerFeatures(
          source.url, '1=1', geom,
          'esriGeometryPolygon', 'esriSpatialRelIntersects', '*', true, 0, 2000, false,
        );
        if (!res.success || !res.data) continue;
        for (const f of res.data) {
          const name = String(f.attributes[source.displayField] ?? '').trim();
          if (!name || name.length < 2) continue;
          all.push({
            id: `${source.name}_${f.attributes.OBJECTID ?? f.attributes.objectid ?? 0}`,
            name,
            sourceName: source.name,
            geometry: f.geometry,
            attributes: f.attributes,
          });
        }
      } catch { /* skip */ }
    }

    this.setState({ ...this.getStateValue(), results: all, loading: false, selectedResult: null });
  }

  private async _loadCache(): Promise<void> {
    if (this._cache.length > 0 || this._cacheLoading) return;
    this._cacheLoading = true;
    const dptoFilter = "(UPPER(nom_dpto) LIKE '%JUNIN%' OR UPPER(nom_dpto) LIKE '%JUNÍN%')";
    const promises = COMMUNITY_SOURCES.map(source =>
      this.queryRepo.queryLayer(source.url, dptoFilter, '*', true, 4326).then(res => {
        if (!res.success || !res.data) return;
        for (const feature of res.data) {
          const name = String(feature.attributes[source.displayField] ?? '').trim();
          if (!name || name.length < 2) continue;
          this._cache.push({
            id: `${source.name}_${feature.attributes.OBJECTID ?? feature.attributes.objectid ?? 0}`,
            name,
            sourceName: source.name,
            geometry: feature.geometry,
            attributes: feature.attributes,
          });
        }
      })
    );
    await Promise.allSettled(promises);
    this._cacheLoading = false;
  }

  selectResult(result: GeolocatorEntity): void {
    // Don't override an active intervention
    if (this.interventionProfile.active()) return;

    this.removeHighlight();
    this.setState({ ...this.getStateValue(), selectedResult: result });

    const view = this.mapProvider.state().view;
    const { geometry } = result;
    if (!view || !geometry) return;

    const isPoint = geometry.x !== undefined;
    let graphicGeometry: any;

    if (isPoint) {
      graphicGeometry = new Point({
        x: geometry.x, y: geometry.y,
        spatialReference: geometry.spatialReference ?? { wkid: 4326 },
      });
      view.goTo({ target: graphicGeometry, scale: 25000 }).catch(() => {});
    } else {
      graphicGeometry = Polygon.fromJSON({
        ...geometry,
        spatialReference: geometry.spatialReference ?? { wkid: 4326 },
      });
      const extent = graphicGeometry.extent;
      if (extent) view.goTo(extent.expand(1.5)).catch(() => {});
    }

    this.fullExtent.setGeometry(graphicGeometry);

    const graphic = new Graphic({
      geometry: graphicGeometry,
      symbol: isPoint
        ? { type: 'simple-marker', style: 'circle', color: [0, 0, 0, 0], size: 24, outline: { color: [255, 120, 0], width: 3 } }
        : { type: 'simple-fill', color: [0, 0, 0, 0], outline: { color: [255, 120, 0], width: 3 } },
    });
    view.graphics.add(graphic);
    this.highlightGraphics = [graphic];
    setTimeout(() => this.startBlink(graphic, view, isPoint), 600);

    this.runIntersection(result);
  }

  private async runIntersection(result: GeolocatorEntity): Promise<void> {
    const source = COMMUNITY_SOURCES.find(s => s.name === result.sourceName);
    const feature: FeatureEntity = {
      attributes: result.attributes as Record<string, any>,
      geometry: result.geometry,
    };
    if (source) (feature as any)._url = source.url;
    await this.intersectionService.analyze(feature, INTERSECTION_TARGETS, '1=1');
    const counts = this.intersectionService.counts();
    if (counts.length > 0) {
      this.executive.applyIntersectionOverride(counts);
    }
    this.intersectionService.clear();

    this.communityProfileService.clear();
    this.rightPanel.notifier.showDetail('community-profile', 'Perfil Comunidad');
    this.communityProfileService.compute(feature);
  }

  private startBlink(graphic: Graphic, view: any, isPoint: boolean): void {
    const BLINK_MS = 180;
    const DURATION_MS = 3000;
    let elapsed = 0;
    let on = true;

    this.pulseInterval = setInterval(() => {
      elapsed += BLINK_MS;
      on = !on;
      const alpha = on ? 1 : 0;
      graphic.symbol = isPoint
        ? { type: 'simple-marker', style: 'circle', color: [0, 0, 0, 0], size: on ? 24 : 0, outline: { color: [0, 204, 255, alpha], width: 3 } } as any
        : { type: 'simple-fill', color: [0, 0, 0, 0], outline: { color: [0, 204, 255, alpha], width: 3 } } as any;
      if (elapsed >= DURATION_MS) {
        clearInterval(this.pulseInterval);
        this.pulseInterval = null;
        graphic.symbol = isPoint
          ? { type: 'simple-marker', style: 'circle', color: [0, 0, 0, 0], size: 24, outline: { color: [0, 204, 255, 1], width: 3 } } as any
          : { type: 'simple-fill', color: [0, 0, 0, 0], outline: { color: [0, 204, 255, 1], width: 3 } } as any;
      }
    }, BLINK_MS);
  }

  clearSearch(): void {
    this.removeHighlight();
    this.executive.clearIntersectionOverride();
    for (const key of ['intervenciones', 'compromisos', 'espacios']) {
      this.executive.refreshTableFilter(key, undefined);
    }
    this.intersectionService.clear();
    this.communityProfileService.clear();
    this.rightPanel.notifier.clearDetail();
    this.setState({
      ...this.getStateValue(),
      results: [],
      selectedResult: null,
    });
  }

  private removeHighlight(): void {
    if (this.pulseInterval) {
      clearInterval(this.pulseInterval);
      this.pulseInterval = null;
    }
    if (this.highlightGraphics.length > 0) {
      const view = this.mapProvider.state().view;
      if (view) view.graphics.removeMany(this.highlightGraphics);
      this.highlightGraphics = [];
    }
  }

}
